import { useEffect, useState } from 'react';
import type { ChangeEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import type { SupportDialogContext, SupportDialogValues, SupportSeverity } from './SupportTypes';

interface SupportDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: SupportDialogValues) => Promise<void> | void;
  onEmailSupport: (values: SupportDialogValues) => void;
  submitting?: boolean;
  error?: string | null;
  success?: string | null;
  context: SupportDialogContext;
}

const defaultValues: SupportDialogValues = {
  category: 'General Support',
  severity: 'Medium',
  subject: '',
  description: '',
};

const severityOptions: SupportSeverity[] = ['Low', 'Medium', 'High', 'Urgent'];

const SupportDialog = ({
  open,
  onClose,
  onSubmit,
  onEmailSupport,
  submitting = false,
  error,
  success,
  context,
}: SupportDialogProps) => {
  const [values, setValues] = useState<SupportDialogValues>(defaultValues);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setValues(defaultValues);
      setValidationError(null);
    }
  }, [open]);

  const handleChange = (field: keyof SupportDialogValues, value: string) => {
    setValues((current: SupportDialogValues) => ({ ...current, [field]: value }));
    if (validationError) {
      setValidationError(null);
    }
  };

  const handleSubmit = async () => {
    if (!values.subject.trim()) {
      setValidationError('Subject is required.');
      return;
    }

    if (!values.description.trim()) {
      setValidationError('Description is required.');
      return;
    }

    await onSubmit({
      ...values,
      subject: values.subject.trim(),
      description: values.description.trim(),
    });
  };

  const handleCategoryChange = (event: SelectChangeEvent<string>) => {
    handleChange('category', event.target.value);
  };

  const handleSeverityChange = (event: SelectChangeEvent<string>) => {
    handleChange('severity', event.target.value as SupportSeverity);
  };

  const handleSubjectChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleChange('subject', event.target.value);
  };

  const handleDescriptionChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleChange('description', event.target.value);
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Contact Support</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Submit a support request with tenant and portal context attached automatically.
          </Typography>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Source App</Typography>
              <Typography variant="body2">{context.sourceApp}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Portal Type</Typography>
              <Typography variant="body2">{context.portalType}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Tenant ID</Typography>
              <Typography variant="body2">{context.tenantId || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Signed-in Email</Typography>
              <Typography variant="body2">{context.userEmail || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Role</Typography>
              <Typography variant="body2">{context.userRole || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Subscription Tier</Typography>
              <Typography variant="body2">{context.subscriptionTier || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Marketplace Plan Id</Typography>
              <Typography variant="body2">{context.marketplacePlanId || 'N/A'}</Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="support-category-label">Category</InputLabel>
                <Select
                  labelId="support-category-label"
                  label="Category"
                  value={values.category}
                  onChange={handleCategoryChange}
                >
                  <MenuItem value="General Support">General Support</MenuItem>
                  <MenuItem value="Authentication">Authentication</MenuItem>
                  <MenuItem value="Deployment">Deployment</MenuItem>
                  <MenuItem value="Branding">Branding</MenuItem>
                  <MenuItem value="Marketplace">Marketplace</MenuItem>
                  <MenuItem value="Billing">Billing</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="support-severity-label">Severity</InputLabel>
                <Select
                  labelId="support-severity-label"
                  label="Severity"
                  value={values.severity}
                  onChange={handleSeverityChange}
                >
                  {severityOptions.map(option => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Subject"
                value={values.subject}
                onChange={handleSubjectChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={values.description}
                onChange={handleDescriptionChange}
                fullWidth
                multiline
                minRows={5}
                required
                helperText={context.pageUrl ? `Current page: ${context.pageUrl}` : undefined}
              />
            </Grid>
          </Grid>

          {(validationError || error) && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {validationError || error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {success}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Close</Button>
        <Button onClick={() => onEmailSupport(values)} disabled={submitting}>Email Support</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
          {submitting ? 'Submitting...' : 'Submit Ticket'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SupportDialog;
